import type { FormInstance, TableInstance, FormItemProp } from 'element-plus'
import {
  ref,
  computed,
  type Ref,
  reactive,
  useTemplateRef,
  getCurrentInstance,
  toRaw,
  useAttrs,
  watch,
} from 'vue'
import { useRequest } from '@el-plus/hooks/use-request'
import type { TableColumn, TableProps, TableScope } from './table'
import type { ButtonProps } from '@el-plus/components/buttons'
import type { FormProps } from '@el-plus/components/form'
import { bus } from '@el-plus/utils'
import { merge } from 'lodash-unified'
import {
  prepareProps,
  prepareEvents,
  prepareClassNames,
  prepareStyles,
} from '@el-plus/utils/props'
import { cloneDeep } from 'lodash-unified'
import { EditPen } from '@element-plus/icons-vue'
import { ElIcon } from 'element-plus'
import { useFormDialog } from '@el-plus/hooks/dialog/use-form-dialog'

export type ISearch = (init: boolean, isPagination?: boolean) => void
export const useTable = (props: TableProps) => {
  const elFormRef = useTemplateRef<FormInstance>('elFormRef')
  const elTableRef = useTemplateRef<TableInstance>('elTableRef')
  const allData = ref<Record<string, unknown>[]>([]) // 所有数据
  const customData = ref<Record<string, unknown>[]>([]) // 当前展示数据
  const apiTotal = ref(0) // 后端分页total
  const { loading, requestFn } = useRequest({
    api: props.api,
    method: props.method,
    reqData: props.reqData,
    reqParams: props.reqParams,
    reqBefore: props.reqBefore,
    reqAfter: props.reqAfter,
    cancelToken: true,
    hooks: {
      success(data) {
        if (Array.isArray(data)) {
          allData.value = data
        } else if (data && typeof data === 'object') {
          const rawData = data as Record<string, any>
          allData.value = rawData?.list || rawData?.data || []
        } else {
          allData.value = []
        }
        if (props.isFrontPage) {
          frontPaginationHandler()
        }else{
          customData.value = allData.value
          apiTotal.value = (data as { total: number }).total || 0
        }
      },
      fail(error) {
        console.error(error)
      },
    },
  })
  // 自定义列
  const customColumns = ref<TableColumn[]>([])
  // 加载列表
  const attrs = useAttrs()
  const search: ISearch = async (init = false, isPagination = false) => {
    const beforeSearch = attrs['before-search']
    if (typeof beforeSearch === 'function') {
      await beforeSearch?.()
    }
    if (init) {
      pagination.pageNo = 1
    }
    // 前端分页+点击分页/切换页码
    if (isPagination && props.isFrontPage) {
      frontPaginationHandler()
    } else {
      if (props.api) {
        requestFn!({
          pageNo: pagination.pageNo,
          pageSize: pagination.pageSize,
          pageNum: pagination.pageNo,
        })
      } 
    }
  }
  const { pagination, paginationProps } = usePagination(
    props,
    search,
    allData,
    apiTotal,
  )
  // 前端分页
  const frontPaginationHandler = () => {
    const { pageNo, pageSize } = pagination
    const data = allData.value.filter((item, index) => {
      return index < pageNo * pageSize && index >= (pageNo - 1) * pageSize
    })
    customData.value = data
  }
  if (!props.api) {
    watch(
      () => props.data,
      (newValue) => {
        allData.value = newValue as Record<string, any>[]
        if (props.isFrontPage) {
          frontPaginationHandler()
        } else {
          customData.value = allData.value
        }
      },
      {
        deep: true,
        immediate: true,
      },
    )
  }

  // 初始化加载
  ;(async () => {
    if (props.isInitSearch) {
      if (props.customColumnModule) {
        loading!.value = true
        const columns = await bus.once('loadedCustomColumn')
        loading!.value = false
        customColumns.value = columns
        bus.on('loadedCustomColumn', (columns: TableColumn[]) => {
          customColumns.value = columns
        })
      }
      search(true)
    }
  })()

  // 校验
  const validate = () => {
    return elFormRef.value!.validate()
  }
  // 重置校验
  const resetFields = (props: FormItemProp) => {
    return elFormRef.value!.resetFields(props)
  }
  // 清理选择,用于多选表格，清空用户的选择
  const clearSelection = () => {
    return elTableRef.value!.clearSelection()
  }
  // 清理某个字段的表单验证信息
  const clearValidate = (props: FormItemProp) => {
    return elFormRef.value!.clearValidate(props)
  }
  // 获取选中数据
  const getSelectionRows = () => {
    return elTableRef.value!.getSelectionRows()
  }
  // 格式化操作按钮
  const formatActionButtons = (list: ButtonProps[], scope: TableScope) => {
    return list.map((item) => {
      if (item.onClick) {
        return {
          ...item,
          onClick: item.onClick.bind(null, scope),
        }
      }
      return item
    })
  }

  return {
    loading,
    data: customData,
    search,
    pagination,
    paginationProps,
    validate,
    clearSelection,
    resetFields,
    clearValidate,
    getSelectionRows,
    elFormRef,
    elTableRef,
    formatActionButtons,
    customColumns: customColumns as unknown as Ref<TableColumn[]>,
  }
}
const usePagination = function (
  props: TableProps,
  search: ISearch,
  allData: Ref<Record<string, any>[]>,
  apiTotal: Ref<number>,
) {
  const defaultPaginationProps = props.paginationProps || {}
  const pageSizes = defaultPaginationProps.pageSizes || [20, 50, 100, 200]
  const pagination = reactive({
    total: computed(() => {
      return !props.isFrontPage && props.api ? apiTotal.value : allData.value.length
    }),
    pageNo: 1,
    pageSize: pageSizes[0],
  })
  const paginationProps = computed(() => {
    const onSizeChange = defaultPaginationProps.onSizeChange
    const onCurrentChange = defaultPaginationProps.onCurrentChange
    return {
      layout: 'total, sizes, prev, pager, next, jumper',
      ...defaultPaginationProps,
      pageSizes,
      onSizeChange: (value: number) => {
        onSizeChange && onSizeChange(value)
        search(true, true)
      },
      onCurrentChange: (value: number) => {
        onCurrentChange && onCurrentChange(value)
        search(false, true)
      },
    }
  })
  return {
    pagination,
    paginationProps,
  }
}

export const useTableColumn = (
  props: TableProps,
  tableData: Ref<TableProps['data']>,
  customColumns: Ref<TableColumn[]>,
) => {
  const instance = getCurrentInstance()
  const columnProps = (column: TableColumn) => {
    column.props = column.props || {}
    const attrs = {
      'show-overflow-tooltip': true,
      align: column.align || props.align,
      minWidth: column.minWidth || props.minWidth,
      // ...prepareEvents<TableEmits>(emit, tableEmits),
    } as TableColumn
    if (!column.renderHeader) {
      // 必填
      let required = column.required
      if (typeof column.required === 'function') {
        required = column.required()
      }
      // 批量编辑
      const editable = column.editable
      attrs['renderHeader'] = () => {
        return (
          <span>
            {required ? (
              <span style="color:var(--el-color-danger)"> * </span>
            ) : (
              ''
            )}
            <span
              class={{ 'is-edit': editable }}
              onClick={
                editable
                  ? async () => {
                      const cloneColumn = { ...column }
                      delete cloneColumn.editable
                      const rawRender = cloneColumn.render
                      const formData = reactive({})
                      if (rawRender) {
                        cloneColumn.render = () => {
                          return rawRender({
                            row: formData,
                          })
                        }
                      }
                      const formDialog = useFormDialog({
                        title: cloneColumn.label,
                        dialogProps: {
                          destroyOnClose: true,
                          instance,
                        },
                        formItemList: [
                          cloneColumn,
                        ] as FormProps['formItemList'],
                        formData,
                        // formData: reactive({}),
                      })
                      const data = (await formDialog.open()) as Record<
                        string,
                        any
                      >
                      ;(tableData.value || []).forEach((item) => {
                        item[column.prop!] = data![column.prop!]
                      })
                      if (typeof editable === 'function') {
                        editable(toRaw(data))
                      }
                    }
                  : void 0
              }
            >
              {column.label}
              {editable ? (
                <ElIcon>
                  <EditPen />
                </ElIcon>
              ) : (
                ''
              )}
            </span>
          </span>
        )
      }
    }
    if (column.filter) {
      const arr = tableData.value!.map((item) => item[column.prop!])
      attrs.filters = [...new Set(arr)].map((item) => {
        return {
          text: item,
          value: item,
        }
      })
      attrs.filterMethod = (value, row) => {
        return row[column.prop!] === value
      }
    }
    if (column.type === 'EpLink') {
      attrs.className = 'table-column-link'
    }

    return {
      ...attrs,
      ...prepareProps(column),
    }
  }
  const columnList = computed(() => {
    const columns = props.customColumnModule
      ? customColumns.value
      : props.columns
    return columns!
      .map((column: TableColumn) => {
        const newColumn = columnProps(column)
        if (props.formatColumns!.length) {
          const formatColumn = props.formatColumns!.find(
            (item) => item.prop === column.prop,
          )
          if (formatColumn) {
            // newColumn = { ...newColumn, ...formatColumn }
            merge(newColumn, formatColumn)
          }
        }
        return newColumn
      })
      .filter((column) => {
        if ('show' in column) {
          return typeof column.show === 'function'
            ? column.show(column)
            : column.show
        } else if ('hide' in column) {
          return typeof column.hide === 'function'
            ? !column.hide(column)
            : !column.hide
        }
        return true
      })
  })
  return {
    columnList,
  }
}

export const useTableFormItem = (props: TableProps) => {
  const formItemProps = (scope: TableScope, rawItem: TableColumn) => {
    const item = cloneDeep(rawItem)
    let required = item.required
    if (typeof required === 'function') {
      required = required()
    }
    if (item.cellRequired) {
      required = item.cellRequired(scope)
    }
    let disabled = props.disabled
    if ('disabled' in item) {
      if (typeof item.disabled === 'function') {
        disabled = item.disabled(scope)
      } else {
        disabled = item.disabled || false
      }
    }
    if (item.type === 'EpSelect' && item.props) {
      if ('options' in item.props && typeof item.props.options === 'function') {
        item.props.options = item.props.options(scope)
      }
      if (
        'disabledOption' in item.props &&
        typeof item.props.disabledOption === 'function'
      ) {
        const disabledOption = item.props.disabledOption
        item.props.disabledOption = (...args: unknown[]) =>
          disabledOption(...args, scope)
      }
      if ('desc' in item.props && typeof item.props.desc === 'function') {
        item.props.desc = item.props.desc(scope)
      }
    }

    if (item.type === 'EpButtons' && item.props) {
      item.props.list.forEach((btnItem: ButtonProps) => {
        if (btnItem.prop) {
          btnItem.name = scope.row[btnItem.prop] as string
        }
        if (btnItem.onClick) {
          btnItem.onClick = btnItem.onClick.bind(null, scope)
        }
      })
    }
    if (item.type === 'EpLink') {
      ;(item.props ? item.props : (item.props = {})).content =
        item.props.content || scope.row[item.prop!]
    }
    for (const key in item) {
      if (key.startsWith('on') && typeof item[key] === 'function') {
        const fn = item[key]
        item[key] = (...args: unknown[]) => {
          let event: Event | unknown
          if (args[0] instanceof Event) {
            // 删除 event
            event = args.shift()
          }
          return fn(...args, scope, event)
        }
      }
    }
    return {
      ...prepareProps(item),
      isShowLabel: false,
      required,
      hideRequiredAsterisk: 'cellRequired' in item ? false : required,
      prop: `table.${scope.$index}.${item.prop}`,
      tableProp: item.prop, // table里的表单需要绑定的prop
      props: {
        ...item.props,
        disabled,
      },
    }
  }
  return {
    formItemProps,
  }
}
